import { useMutation } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'store/hooks';
import { Alert } from '../models';

const createAlert = async (
    payload: Partial<Alert>,
    source: string,
    sourceId: string | number,
): Promise<Alert> => {
    const response = await RQAPICallWithData<Alert>({
        endpoint: `alerts/${source}/${sourceId}`,
        method: 'POST',
        body: payload,
    });
    return response.content.data;
};

/**
 * RQ Hook to create a new alert based on the relevant data.
 * @param {string} source - The source that will carry the new alert
 * @param {string | number} sourceId - The id of this source
 * @param {(alert: Alert) => void} onSuccess - A callback function to be executed on success
 */
export const useCreateAlert = (
    source: string,
    sourceId: string | number,
    onSuccess: (alert?: Alert) => void,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (payload: Partial<Alert>) =>
            createAlert(payload, source, sourceId),

        onSuccess: (alert) => {
            dispatch.vision.notifyUser({
                message: t('alert.create.success', {
                    name: alert.name,
                }),
                snackType: 'success',
            });
            onSuccess(alert);
        },
        meta: { useGlobalOnError: true },
    });
};
